import React, { useState, useContext, useEffect } from "react";
import { CartContext } from "../context/CartContext";
import { useNavigate } from "react-router-dom";
import { host } from "../config/Config";
import "../pages/educations/style.css";
import "./CheckoutPage.css";
import Logo from "../resources/logo.png";

const CheckoutPage = () => {
    const { cartItems, clearCart } = useContext(CartContext);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        password: "",
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [loginPassword, setLoginPassword] = useState("");
    const [loginError, setLoginError] = useState("");
    const [isNavigatingToConfirmation, setIsNavigatingToConfirmation] =
        useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        // Redirect if cart is empty on initial load (but not if we're navigating to confirmation)
        if (
            (!cartItems || cartItems.length === 0) &&
            !isNavigatingToConfirmation
        ) {
            navigate("/educations");
            return;
        }

        // Check if user is already logged in as education user
        const userType = localStorage.getItem("zajednica-user-type");
        const token = localStorage.getItem("zajednica-token");

        if (token && userType === "education") {
            setIsLoggedIn(true);
            // Pre-fill form with logged in user data
            const name = localStorage.getItem("zajednica-name");
            const phone = localStorage.getItem("zajednica-phone");

            // Set name and phone immediately
            setFormData((prev) => ({
                ...prev,
                name: name || "",
                phone: phone || "",
            }));

            // Fetch user's email from backend asynchronously
            const fetchUserEmail = async () => {
                try {
                    const response = await fetch(
                        `${host}/api/v1/users/by-username/${phone}`
                    );
                    if (response.ok) {
                        const userData = await response.json();
                        if (!userData.error && userData.email) {
                            setFormData((prev) => ({
                                ...prev,
                                email: userData.email || "",
                            }));
                        }
                    }
                } catch (error) {
                    console.error("Error fetching user email:", error);
                }
            };

            if (phone) {
                fetchUserEmail();
            }
        }
    }, [navigate, cartItems]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: "",
            }));
        }
    };

    const checkPhoneExists = async (phone) => {
        try {
            const response = await fetch(
                `${host}/api/v1/users/by-username/${phone}`
            );

            if (response.ok) {
                const data = await response.json();
                // Check if response has an error field
                if (data.error) {
                    return false; // Phone doesn't exist
                }
                return true; // Phone exists
            }
            return false;
        } catch (error) {
            console.error("Error checking phone:", error);
            return false;
        }
    };

    const handlePhoneBlur = async () => {
        // Skip check if user is already logged in
        if (isLoggedIn) return;

        const phone = formData.phone.trim();
        if (!phone) return;

        const phoneExists = await checkPhoneExists(phone);
        if (phoneExists) {
            setShowLoginModal(true);
        }
    };

    const handleLogin = async () => {
        setLoginError("");

        try {
            const response = await fetch(`${host}/api/v1/auth/login`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    username: formData.phone,
                    password: loginPassword,
                }),
            });

            if (response.ok) {
                const result = await response.json();

                // Store login info
                localStorage.setItem("zajednica-token", result.token);
                localStorage.setItem("zajednica-phone", formData.phone);
                localStorage.setItem("zajednica-name", result.name);
                localStorage.setItem(
                    "zajednica-user-type",
                    result.isEducationUser === true ? "education" : "regular"
                );

                // Pre-fill form with logged in user data
                setFormData((prev) => ({
                    ...prev,
                    name: result.name || prev.name,
                }));

                setIsLoggedIn(true);
                setShowLoginModal(false);
                setLoginPassword("");
            } else {
                setLoginError("Pogrešna šifra");
            }
        } catch (error) {
            console.error("Login error:", error);
            setLoginError("Došlo je do greške");
        }
    };

    const handleContinueAsNew = () => {
        setShowLoginModal(false);
        setLoginPassword("");
        setLoginError("");
        // Focus on phone input so user can change it
        document.getElementById("phone")?.focus();
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = "Ime je obavezno";
        }

        if (!formData.email.trim()) {
            newErrors.email = "Email je obavezan";
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = "Unesite validan email";
        }

        if (!formData.phone.trim()) {
            newErrors.phone = "Telefon je obavezan";
        } else if (!/^[+]?[\d\s\-()]+$/.test(formData.phone)) {
            newErrors.phone = "Unesite validan broj telefona";
        }

        // Password is only required if not logged in
        if (!isLoggedIn) {
            if (!formData.password.trim()) {
                newErrors.password = "Šifra je obavezna";
            } else if (formData.password.length < 6) {
                newErrors.password = "Šifra mora imati najmanje 6 karaktera";
            }
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = validateForm();

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Check if phone exists before submitting (if not logged in)
        if (!isLoggedIn) {
            const phoneExists = await checkPhoneExists(formData.phone.trim());
            if (phoneExists) {
                setShowLoginModal(true);
                return; // Don't proceed with submission
            }
        }

        setIsSubmitting(true);

        try {
            // Get selected slots and start dates from localStorage
            const selectedSlotsJson = localStorage.getItem("selectedSlots");
            const selectedSlots = selectedSlotsJson
                ? JSON.parse(selectedSlotsJson)
                : {};

            const selectedStartDatesJson =
                localStorage.getItem("selectedStartDates");
            const selectedStartDates = selectedStartDatesJson
                ? JSON.parse(selectedStartDatesJson)
                : {};

            // Prepare education items with slot information and start dates
            const educationItems = cartItems.map((item) => {
                const baseItem = {
                    educationType: item.id,
                    price: item.price,
                };

                // Add educationSlotId only if it exists (for workshop educations)
                if (selectedSlots[item.id]) {
                    baseItem.educationSlotId = selectedSlots[item.id];
                }

                // Add startDate if it exists (for beginner and one-on-one educations)
                if (selectedStartDates[item.id]) {
                    baseItem.startDate = selectedStartDates[item.id];
                }

                return baseItem;
            });

            // Prepare application data
            const applicationData = {
                email: formData.email,
                name: formData.name,
                phone: formData.phone,
                password: formData.password,
                educationItems: educationItems,
            };

            // Submit education application
            const response = await fetch(
                `${host}/api/v1/education-applications`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(applicationData),
                }
            );

            if (response.ok) {
                const data = await response.json();
                console.log("Applications created:", data);

                // Set flag to prevent redirect to /educations
                setIsNavigatingToConfirmation(true);

                // Add artificial delay for better UX (2-3 seconds)
                await new Promise((resolve) => setTimeout(resolve, 2500));

                // Clear cart and selected data
                clearCart();
                localStorage.removeItem("selectedSlots");
                localStorage.removeItem("selectedStartDates");

                // Always go to confirmation page to show payment info
                // The confirmation page will handle skipping login if already logged in
                navigate("/education-confirmation", {
                    state: {
                        phone: formData.phone,
                        password: formData.password,
                        isAlreadyLoggedIn: isLoggedIn,
                    },
                });
            } else {
                // Handle error response
                let errorMessage =
                    "Došlo je do greške. Molimo pokušajte ponovo.";
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.message || errorMessage;
                } catch (parseError) {
                    console.error("Error parsing error response:", parseError);
                }
                alert(errorMessage);
            }
        } catch (error) {
            console.error("Error submitting application:", error);
            alert("Došlo je do greške. Molimo pokušajte ponovo.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className='page'>
            <div className='checkout-hero-wrapper'>
                <div className='checkout-hero-content'>
                    <h1>Finalizuj</h1>
                    <h1>Narudžbinu</h1>
                </div>
            </div>
            <div className='items' style={{ minHeight: "60vh" }}>
                <div className='checkout-container'>
                    <div className='checkout-form-wrapper'>
                        <form onSubmit={handleSubmit} className='checkout-form'>
                            <div className='form-group'>
                                <label htmlFor='name'>Ime i Prezime</label>
                                <input
                                    type='text'
                                    id='name'
                                    name='name'
                                    value={formData.name}
                                    onChange={handleChange}
                                    className={errors.name ? "error" : ""}
                                    placeholder='Unesite vaše ime i prezime'
                                />
                                {errors.name && (
                                    <span className='error-message'>
                                        {errors.name}
                                    </span>
                                )}
                            </div>

                            <div className='form-group'>
                                <label htmlFor='email'>Email</label>
                                <input
                                    type='email'
                                    id='email'
                                    name='email'
                                    value={formData.email}
                                    onChange={handleChange}
                                    className={errors.email ? "error" : ""}
                                    placeholder='primer@email.com'
                                />
                                {errors.email && (
                                    <span className='error-message'>
                                        {errors.email}
                                    </span>
                                )}
                            </div>

                            <div className='form-group'>
                                <label htmlFor='phone'>Telefon</label>
                                <input
                                    type='tel'
                                    id='phone'
                                    name='phone'
                                    value={formData.phone}
                                    onChange={handleChange}
                                    onBlur={handlePhoneBlur}
                                    className={errors.phone ? "error" : ""}
                                    placeholder='PRIMER : 060 123 4567'
                                    autoComplete='tel'
                                />
                                {errors.phone && (
                                    <span className='error-message'>
                                        {errors.phone}
                                    </span>
                                )}
                            </div>

                            <div className='form-group'>
                                <label htmlFor='password'>Šifra</label>
                                <input
                                    type='password'
                                    id='password'
                                    name='password'
                                    value={formData.password}
                                    onChange={handleChange}
                                    className={errors.password ? "error" : ""}
                                    placeholder={
                                        isLoggedIn
                                            ? "Već ste prijavljeni"
                                            : "Unesite šifru (min. 6 karaktera)"
                                    }
                                    disabled={isLoggedIn}
                                />
                                {errors.password && (
                                    <span className='error-message'>
                                        {errors.password}
                                    </span>
                                )}
                            </div>

                            <div className='form-actions'>
                                <button
                                    type='button'
                                    className='back-btn'
                                    onClick={() => navigate("/educations/cart")}
                                >
                                    Nazad
                                </button>
                                <button
                                    type='submit'
                                    className='submit-btn'
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting
                                        ? "Obrađujem..."
                                        : "Potvrdi Narudžbinu"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* Login Modal */}
            {showLoginModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.8)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                    }}
                >
                    <div
                        style={{
                            backgroundColor: "#1e1e1e",
                            padding: "40px",
                            borderRadius: "12px",
                            maxWidth: "450px",
                            width: "90%",
                            border: "1px solid #333",
                        }}
                    >
                        <h2
                            style={{
                                color: "#be9455",
                                marginBottom: "20px",
                                fontSize: "24px",
                            }}
                        >
                            Korisnik već postoji
                        </h2>
                        <p
                            style={{
                                color: "#ccc",
                                marginBottom: "25px",
                                lineHeight: "1.6",
                            }}
                        >
                            Ovaj broj telefona je već registrovan. Molimo vas da
                            se prijavite ili nastavite kao novi korisnik.
                        </p>

                        <div style={{ marginBottom: "20px" }}>
                            <label
                                style={{
                                    color: "#ccc",
                                    display: "block",
                                    marginBottom: "8px",
                                }}
                            >
                                Šifra
                            </label>
                            <input
                                type='password'
                                value={loginPassword}
                                onChange={(e) =>
                                    setLoginPassword(e.target.value)
                                }
                                onKeyPress={(e) => {
                                    if (e.key === "Enter") handleLogin();
                                }}
                                placeholder='Unesite vašu šifru'
                                style={{
                                    width: "100%",
                                    padding: "12px",
                                    backgroundColor: "#2a2a2a",
                                    border: "1px solid #444",
                                    borderRadius: "6px",
                                    color: "#fff",
                                    fontSize: "16px",
                                }}
                            />
                            {loginError && (
                                <span
                                    style={{
                                        color: "#ff6b6b",
                                        fontSize: "14px",
                                        marginTop: "8px",
                                        display: "block",
                                    }}
                                >
                                    {loginError}
                                </span>
                            )}
                        </div>

                        <div
                            style={{
                                display: "flex",
                                gap: "15px",
                                marginTop: "30px",
                            }}
                        >
                            <button
                                onClick={handleLogin}
                                style={{
                                    flex: 1,
                                    backgroundColor: "#be9455",
                                    color: "#121212",
                                    border: "none",
                                    padding: "14px",
                                    fontSize: "16px",
                                    fontWeight: "bold",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                }}
                            >
                                Prijavi se
                            </button>
                            <button
                                onClick={handleContinueAsNew}
                                style={{
                                    flex: 1,
                                    backgroundColor: "#333",
                                    color: "#ccc",
                                    border: "1px solid #555",
                                    padding: "14px",
                                    fontSize: "16px",
                                    fontWeight: "bold",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                }}
                            >
                                Promeni telefon
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CheckoutPage;
